// 低解像度へ縮小してから、補間せず拡大する。タイルを抜くマスクは使わない。
export function drawPixelatedCopy(source, output, sample, blockSize, ratio) {
  const context = output.getContext('2d');
  context.clearRect(0, 0, output.width, output.height);
  if (blockSize <= 1) {
    context.drawImage(source, 0, 0);
    return;
  }
  sample.width = Math.max(1, Math.ceil(source.width / (blockSize * ratio)));
  sample.height = Math.max(1, Math.ceil(source.height / (blockSize * ratio)));
  const sampling = sample.getContext('2d');
  sampling.imageSmoothingEnabled = true;
  sampling.drawImage(source, 0, 0, sample.width, sample.height);
  context.imageSmoothingEnabled = false;
  context.drawImage(sample, 0, 0, sample.width, sample.height, 0, 0, output.width, output.height);
}

export function createCopyPixelator(copy) {
  const document = copy.ownerDocument;
  const view = document.defaultView;
  const source = document.createElement('canvas');
  const layer = document.createElement('canvas');
  const sample = document.createElement('canvas');
  layer.className = 'copy-mosaic-layer';
  layer.setAttribute('aria-hidden', 'true');
  let ratio = 1;
  let frame = 0;

  const stop = () => {
    view.cancelAnimationFrame(frame);
    frame = 0;
  };
  const capture = () => {
    stop();
    const box = copy.getBoundingClientRect();
    if (!box.width || !box.height || !source.getContext('2d') || !layer.getContext('2d') || !sample.getContext('2d')) return false;
    ratio = Math.min(view.devicePixelRatio || 1, 2);
    source.width = layer.width = Math.ceil(box.width * ratio);
    source.height = layer.height = Math.ceil(box.height * ratio);
    const context = source.getContext('2d');
    context.scale(ratio, ratio);
    context.fillStyle = view.getComputedStyle(copy).backgroundColor;
    context.fillRect(0, 0, box.width, box.height);

    for (const paragraph of copy.querySelectorAll('p')) {
      const text = paragraph.firstChild;
      if (!text || text.nodeType !== 3) continue;
      const style = view.getComputedStyle(paragraph);
      context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      context.fillStyle = style.color;
      context.textBaseline = 'alphabetic';
      if ('letterSpacing' in context) context.letterSpacing = style.letterSpacing;
      const ascent = context.measureText('国Hg').fontBoundingBoxAscent ?? parseFloat(style.fontSize) * .85;
      const range = document.createRange();
      const lines = [];
      let offset = 0;
      // DOMの実際の改行位置を使い、独自の折り返し計算はしない。
      for (const character of text.textContent) {
        range.setStart(text, offset);
        offset += character.length;
        range.setEnd(text, offset);
        const rect = range.getBoundingClientRect();
        const line = lines.at(-1);
        if (line && Math.abs(line.top - rect.top) < 1) line.text += character;
        else lines.push({ text: character, left: rect.left, top: rect.top });
      }
      for (const line of lines) context.fillText(line.text, line.left - box.left, line.top - box.top + ascent);
    }

    for (const pseudo of ['::before', '::after']) {
      const style = view.getComputedStyle(copy, pseudo);
      const width = parseFloat(style.width) || 0;
      const height = parseFloat(style.height) || 0;
      const left = style.left === 'auto' ? box.width - parseFloat(style.right) - width : parseFloat(style.left);
      const top = style.top === 'auto' ? box.height - parseFloat(style.bottom) - height : parseFloat(style.top);
      for (const side of ['Top', 'Right', 'Bottom', 'Left']) {
        const border = parseFloat(style[`border${side}Width`]) || 0;
        if (!border) continue;
        context.fillStyle = style[`border${side}Color`];
        const horizontal = side === 'Top' || side === 'Bottom';
        context.fillRect(left + (side === 'Right' ? width - border : 0), top + (side === 'Bottom' ? height - border : 0), horizontal ? width : border, horizontal ? border : height);
      }
    }
    copy.append(layer);
    return true;
  };
  const draw = () => {
    const size = Number(view.getComputedStyle(copy).getPropertyValue('--copy-mosaic-size')) || 1;
    drawPixelatedCopy(source, layer, sample, size, ratio);
    frame = view.requestAnimationFrame(draw);
  };
  return {
    capture,
    start() { stop(); draw(); },
    stop,
    destroy() { stop(); layer.remove(); },
  };
}
